import type { NodeDef } from '../core/types';
import { BALANCE } from './balance';

/**
 * Arbre de talents — **COMMENTS v0.4.1 §1.2** (tableau du GD) et DECISIONS §E.
 *
 * 53 nœuds (20 + les 18 de COMMENTS v0.6.0 §1.1 + les 15 de COMMENTS v0.25.0
 * §1.2, « la suite de l'arbre permettant de finir le jeu »), racine comprise,
 * en trois branches. L'arbre de prestige est
 * supprimé : ses nœuds utiles (*Gobelet*, *Contenant hexagonal*) sont repris
 * ici, et l'arbre entier est **conservé d'une run à l'autre** (§1.3).
 *
 * Corrections A2 (paliers incohérents dans le tableau) :
 *  - *Recycleur* et *GachaDice v6* : niveau maximum **1** (aucun effet par niveau) ;
 *  - *Journée Longue* : niveau maximum **5** (+1 s par niveau) ;
 *  - *Puissance des sorts* : niveau maximum **5**, ×(1 + 0,1 × niveau).
 *
 * Corrections A23 (mêmes incohérences dans le tableau v0.6.0) :
 *  - *GachaDice v12*, *Quatuor*, *Larbin*, *Conservation II*, *PentaDice* et
 *    *Conservation III* : niveau maximum **1** (aucun effet par niveau) ;
 *  - *Sorcellerie* : niveau maximum **5**, ×(1 + 0,1 × niveau) — A21.
 *
 * Coût d'un niveau = `baseCost × nodeCostGrowth^(niveau actuel)` (A7) —
 * `core/economy.ts`. `nodeCostGrowth` vaut 1,6 jusqu'en v0.8.0, **1,55**
 * depuis (COMMENTS v0.8.0 §2.1) — voir `data/balance.ts`.
 *
 * v0.26.0 (COMMENTS v0.25.0 §1.2, arbitrage A72) : les quinze nœuds du tableau,
 * mot pour mot, avec les chiffres tranchés par le GD — *Criticité* et *Flux
 * arcanique* +0,05 par niveau (A72-3), *Conservation V* sous *Heptadice*
 * (A72-2), *Esclavagisme* 0,75 + 0,1 × niveau (A72-12).
 *
 * v0.41.0 (A86, 12/10/2026) : *Larcin* +10 % d'or par niveau (+50 % avant,
 * `nodeLarcinGoldPerLevel`) ; *Larbin* passe sous *Conservation* à 600 or
 * (sous *Quatuor* à 1 800 avant) ; *Coup de fouet* passe sous *Quatuor*.
 *
 * v0.43.0 (A89, 13/10/2026) : trois nœuds — *Centésimal* (`shop_v100`, machine
 * de d100), *Avant-garde* (`ars_vanguard`) et *Arrière-garde* (`arc_rearguard`)
 * — soit **56** nœuds.
 *
 * v0.45 (A93, 14/10/2026) : *Centésimal* et *Avant-garde* sont **retirés** (une
 * sauvegarde qui les possède est remboursée, `core/save.ts`) ; *Arrière-garde*
 * reste — soit **54** nœuds, racine comprise (53 talents).
 *
 * v0.46.0 (A94, 15/10/2026) : quatrième branche, l'**Abîme** — dix nœuds en
 * chaîne sous la racine (le lien parent → enfant n'est **pas** une condition
 * d'achat : seule la **porte** compte, A94-3). Chaque nœud porte `gate` (niveau
 * d'Expédition qui ouvre son niveau 1, puis un niveau tous les deux niveaux),
 * `costExpeditions` (3 : prix d'un niveau = 3 × Exp(porte), A94-1) et un
 * `baseCost` nul (le prix vient de la porte, `core/economy.ts`). *Artisan des
 * dés* est **offert** à l'entrée en mode infini (A94-2) ; *Sans fond* est le
 * seul nœud répétable (`maxLevel` infini, porte 29 pour tous ses niveaux,
 * A94-12). Les effets de *Matériau*, *Qui a dit Poker ?*, des deux Grimoires
 * et le bonus à 5 pièces de *Matériologie* sont livrés en **v0.47.0** (A94-17,
 * A95 : `core/materials.ts`, `core/combos.ts`). Soit **64** nœuds, racine
 * comprise (63 talents).
 *
 * Les effets « ×1,05 puis +0,05 par niveau supplémentaire » du tableau sont
 * **linéaires** (×(1 + 0,05 × niveau)), à l'image de l'arbitrage A2 sur
 * *Puissance des sorts* — voir DECISIONS §D.16.
 */
export const NODES: NodeDef[] = [
  {
    id: 'root',
    name: 'GachaDice v4',
    branch: 'root',
    parent: null,
    maxLevel: 1,
    baseCost: 0,
    description: 'Vous débloquez la machine Gachapon de d4.',
  },

  // ── Arsenal : dégâts, vitesse, critiques, puis le Gobelet.
  {
    id: 'ars_dmg',
    name: 'Petit lancer',
    branch: 'arsenal',
    parent: 'root',
    maxLevel: 3,
    baseCost: 15,
    description: 'Ajoute +1 dégât de base du lancer par dé équipé, par niveau.',
  },
  {
    id: 'ars_heavy',
    name: 'Main lestée',
    branch: 'arsenal',
    parent: 'ars_dmg',
    maxLevel: 5,
    baseCost: 50,
    description: 'Multiplie les dégâts du lancer par 1,05, +0,05 par niveau.',
  },
  {
    id: 'ars_fast',
    name: 'Bras rapide',
    branch: 'arsenal',
    parent: 'ars_heavy',
    maxLevel: 5,
    baseCost: 80,
    description: 'Le temps de retour est réduit de 5 % par niveau.',
  },
  {
    id: 'ars_crit',
    name: 'Lancer précis',
    branch: 'arsenal',
    parent: 'ars_fast',
    maxLevel: 5,
    baseCost: 90,
    description: 'Gagne +2 % de chance de coup critique par niveau.',
  },
  {
    id: 'ars_critdmg',
    name: 'Touche critique',
    branch: 'arsenal',
    parent: 'ars_crit',
    maxLevel: 5,
    baseCost: 100,
    description: 'Gagne +5 % de dégâts critiques additifs par niveau.',
  },
  {
    id: 'ars_goblet',
    name: 'Gobelet',
    branch: 'arsenal',
    parent: 'ars_fast',
    maxLevel: 5,
    baseCost: 110,
    description: 'Vous débloquez le gobelet et gagnez 2 dés Basiques par niveau.',
  },
  {
    id: 'ars_hex',
    name: 'Contenant hexagonal',
    branch: 'arsenal',
    parent: 'ars_goblet',
    maxLevel: 5,
    baseCost: 120,
    description: '20 % des dés du contenant deviennent des d6, par niveau.',
  },
  {
    id: 'ars_bowl',
    name: 'Bol',
    branch: 'arsenal',
    parent: 'ars_hex',
    maxLevel: 5,
    baseCost: 800,
    description: 'Vous débloquez le bol : +10 dés Basiques par niveau.',
  },
  {
    id: 'ars_scope',
    name: 'Dé à lunette',
    branch: 'arsenal',
    parent: 'ars_bowl',
    maxLevel: 5,
    baseCost: 900,
    description: 'Gagne +2 % de chance de coup critique par niveau.',
  },
  {
    id: 'ars_magiccrit',
    name: 'Critique Magique',
    branch: 'arsenal',
    parent: 'ars_scope',
    maxLevel: 5,
    baseCost: 950,
    description: 'Multiplie les dégâts critiques par 1,05, +0,05 par niveau.',
  },
  {
    id: 'ars_critmachine',
    name: 'Machine Critique',
    branch: 'arsenal',
    parent: 'ars_scope',
    maxLevel: 5,
    baseCost: 1100,
    description:
      'Lors d’un coup critique, le temps de retour est réduit de 5 % par niveau ' +
      'pendant 5 secondes.',
  },
  {
    id: 'ars_dodeca',
    name: 'Dodécaédrique',
    branch: 'arsenal',
    parent: 'ars_critmachine',
    maxLevel: 5,
    baseCost: 1800,
    description: '20 % des dés du contenant deviennent des d12, par niveau.',
  },
  {
    id: 'ars_bucket',
    name: 'Seau',
    branch: 'arsenal',
    parent: 'ars_dodeca',
    maxLevel: 5,
    baseCost: 3000,
    description: 'Vous débloquez le seau : +50 dés Basiques par niveau.',
  },
  // v0.26.0 — COMMENTS v0.25.0 §1.2 (A72).
  {
    id: 'ars_dicemaster',
    name: 'Dice Master',
    branch: 'arsenal',
    parent: 'ars_bucket',
    maxLevel: 1,
    baseCost: 20000,
    description:
      'Vos dégâts de lancer sont multipliés par la moyenne des raretés de vos dés Maîtres ' +
      'équipés : ×1 Commun, ×1,05 Inhabituel, ×1,1 Rare, ×1,25 Épique, ×1,5 Légendaire, ' +
      '×2 Prismatique.',
  },
  {
    id: 'ars_barrow',
    name: 'Brouette',
    branch: 'arsenal',
    parent: 'ars_bucket',
    maxLevel: 5,
    baseCost: 25000,
    description: 'Vous débloquez la brouette : +500 dés Basiques par niveau.',
  },
  {
    id: 'ars_icosa',
    name: 'Icosaèdrique',
    branch: 'arsenal',
    parent: 'ars_barrow',
    maxLevel: 5,
    baseCost: 30000,
    description: '20 % des dés du contenant deviennent des d20, par niveau.',
  },
  {
    id: 'ars_critstreak',
    name: 'Criticité',
    branch: 'arsenal',
    parent: 'ars_barrow',
    maxLevel: 5,
    baseCost: 60000,
    description:
      'Les coups critiques consécutifs multiplient les dégâts du lancer par 1,02 par niveau ' +
      '(1,10 au niveau 5), une fois par critique consécutif à partir du deuxième, jusqu’à 10 ' +
      'cumuls.',
  },
  {
    id: 'ars_critadv',
    name: 'Avantage Critique',
    branch: 'arsenal',
    parent: 'ars_critstreak',
    maxLevel: 1,
    baseCost: 150000,
    description:
      'Vos coups critiques deviennent avantageux : la chance de critique est tirée deux fois, ' +
      'le lancer est critique si l’un des deux tirages réussit.',
  },
  {
    id: 'ars_cheat',
    name: 'Triche',
    branch: 'arsenal',
    parent: 'ars_icosa',
    maxLevel: 1,
    baseCost: 300000,
    description:
      'Vos dégâts de lancer deviennent avantageux : deux lancers sont effectués et celui au ' +
      'total de dégâts le plus élevé est appliqué.',
  },

  // ── Boutique : or, puis les machines.
  {
    id: 'shop_gold',
    name: 'Butin',
    branch: 'shop',
    parent: 'root',
    maxLevel: 5,
    baseCost: 20,
    description: 'Vous gagnez +10 % d’or à la mort d’un ennemi, par niveau.',
  },
  {
    id: 'shop_double',
    name: 'Mort dorée',
    branch: 'shop',
    parent: 'shop_gold',
    maxLevel: 5,
    baseCost: 30,
    description: '1 % de chance par niveau de gagner le double d’or à la mort d’un ennemi.',
  },
  {
    id: 'shop_recycle',
    name: 'Recycleur',
    branch: 'shop',
    parent: 'shop_gold',
    maxLevel: 1,
    baseCost: 120,
    description: 'Vous débloquez le recycleur de dés.',
  },
  {
    id: 'shop_v6',
    name: 'GachaDice v6',
    branch: 'shop',
    parent: 'shop_recycle',
    maxLevel: 1,
    baseCost: 150,
    description: 'Vous débloquez la machine Gachapon de d6.',
  },
  {
    id: 'shop_cheap',
    name: 'Négociateur',
    branch: 'shop',
    parent: 'shop_double',
    maxLevel: 5,
    baseCost: 80,
    description: 'Réduit de 5 % le coût d’achat des dés, par niveau.',
  },
  {
    id: 'shop_rarity',
    name: 'Modification mécanique',
    branch: 'shop',
    parent: 'shop_v6',
    maxLevel: 5,
    baseCost: 200,
    description:
      'Augmente de +10 % les chances d’obtenir des dés Inhabituels ou plus, par niveau.',
  },
  {
    id: 'shop_larcin',
    name: 'Larcin',
    branch: 'shop',
    parent: 'shop_rarity',
    maxLevel: 5,
    baseCost: 600,
    description: 'Vous gagnez +10 % d’or à la mort d’un ennemi, par niveau.',
  },
  {
    id: 'shop_v12',
    name: 'GachaDice v12',
    branch: 'shop',
    parent: 'shop_larcin',
    maxLevel: 1,
    baseCost: 1400,
    description: 'Vous débloquez la machine Gachapon de d12.',
  },
  {
    id: 'shop_twin',
    name: 'Gagnant-Gagnant',
    branch: 'shop',
    parent: 'shop_v12',
    maxLevel: 5,
    baseCost: 1600,
    description:
      '+1 % de chance par niveau d’obtenir deux dés au lieu d’un aux machines Gachapon.',
  },
  {
    id: 'shop_tinker',
    name: 'Bidouilleur',
    branch: 'shop',
    parent: 'shop_v12',
    maxLevel: 5,
    baseCost: 1600,
    description:
      'Augmente de +10 % les chances d’obtenir des dés Inhabituels ou plus, par niveau ' +
      '(cumulé avec Modification mécanique).',
  },
  {
    id: 'shop_orsplosion',
    name: 'Orsplosion',
    branch: 'shop',
    parent: 'shop_tinker',
    maxLevel: 5,
    baseCost: 1800,
    description: '+2 % de chance par niveau de gagner le double d’or à la mort d’un ennemi.',
  },
  // v0.26.0 — COMMENTS v0.25.0 §1.2 (A72).
  {
    id: 'shop_v20',
    name: 'GachaDice v20',
    branch: 'shop',
    parent: 'shop_orsplosion',
    maxLevel: 1,
    baseCost: 10000,
    description: 'Vous débloquez la machine Gachapon de d20.',
  },
  {
    id: 'shop_loyalty',
    name: 'Carte de fidélité',
    branch: 'shop',
    parent: 'shop_v20',
    maxLevel: 5,
    baseCost: 15000,
    description:
      'Réduit de 5 % le coût d’achat des dés, par niveau (s’additionne avec Négociateur).',
  },
  {
    id: 'shop_rigged',
    name: 'Machine Pipée',
    branch: 'shop',
    parent: 'shop_v20',
    maxLevel: 5,
    baseCost: 20000,
    description:
      'Augmente de +10 % les chances d’obtenir des dés Inhabituels ou plus, par niveau ' +
      '(cumulé avec Modification mécanique et Bidouilleur).',
  },

  // ── Arcanes : chrono, emplacements, sorts.
  {
    id: 'arc_day',
    name: 'Journée longue',
    branch: 'arcane',
    parent: 'root',
    maxLevel: 5,
    baseCost: 15,
    description: 'Augmente le temps d’Expédition de 1 seconde par niveau.',
  },
  {
    id: 'arc_slot2',
    name: 'Mieux vaut être plusieurs',
    branch: 'arcane',
    parent: 'arc_day',
    maxLevel: 1,
    baseCost: 70,
    description: 'Vous débloquez un deuxième emplacement de dé Maître.',
  },
  {
    id: 'arc_power',
    name: 'Puissance des sorts',
    branch: 'arcane',
    parent: 'arc_slot2',
    maxLevel: 5,
    baseCost: 200,
    description: 'Multiplie par 1,1 les effets des sorts, +0,1 par niveau.',
  },
  {
    id: 'arc_slot3',
    name: 'Jamais deux sans trois',
    branch: 'arcane',
    parent: 'arc_slot2',
    maxLevel: 1,
    baseCost: 250,
    description: 'Vous débloquez un troisième emplacement de dé Maître.',
  },
  {
    id: 'arc_keep',
    name: 'Conservation',
    branch: 'arcane',
    parent: 'arc_slot3',
    maxLevel: 1,
    baseCost: 300,
    description:
      'Vous conservez le dé Maître de votre emplacement 1 même après votre mort.',
  },
  {
    id: 'arc_prep',
    name: 'Préparation',
    branch: 'arcane',
    parent: 'arc_power',
    maxLevel: 5,
    baseCost: 350,
    // v0.50.0 (COMMENTS v0.49.1 §1.3, A100-2) : renfort des sorts des
    // emplacements 1 à 3 ; le compteur a disparu.
    description:
      'Les sorts de dé portés par vos dés Maîtres des emplacements 1 à 3 sont renforcés de 10 % par niveau.',
  },
  {
    id: 'arc_slot4',
    name: 'Quatuor',
    branch: 'arcane',
    parent: 'arc_prep',
    maxLevel: 1,
    baseCost: 1500,
    description: 'Vous débloquez un 4e emplacement de dé Maître.',
  },
  {
    id: 'arc_minion',
    name: 'Larbin',
    branch: 'arcane',
    parent: 'arc_keep',
    maxLevel: 1,
    baseCost: 600,
    description:
      'Vous débloquez un larbin : il lance une copie de vos dés à votre place, avec ' +
      '25 % de temps de lancer et de dégâts en moins.',
  },
  {
    id: 'arc_whip',
    name: 'Coup de fouet',
    branch: 'arcane',
    parent: 'arc_slot4',
    maxLevel: 5,
    baseCost: 2000,
    description: 'Le temps de lancer du Larbin est réduit de 5 % par niveau.',
  },
  {
    id: 'arc_slavery',
    name: 'Esclavagisme',
    branch: 'arcane',
    parent: 'arc_whip',
    maxLevel: 5,
    baseCost: 5000,
    description: 'Les dégâts de lancer du Larbin sont augmentés de 10 %, par niveau.',
  },
  {
    id: 'arc_keep2',
    name: 'Conservation II',
    branch: 'arcane',
    parent: 'arc_slot4',
    maxLevel: 1,
    baseCost: 2500,
    description:
      'Vous conservez le dé Maître de votre emplacement 2 même après votre mort.',
  },
  {
    id: 'arc_sorcery',
    name: 'Sorcellerie',
    branch: 'arcane',
    parent: 'arc_keep2',
    maxLevel: 5,
    baseCost: 2800,
    description: 'Multiplie par 1,1 les effets des sorts, +0,1 par niveau.',
  },
  {
    id: 'arc_slot5',
    name: 'PentaDice',
    branch: 'arcane',
    parent: 'arc_keep2',
    maxLevel: 1,
    baseCost: 4000,
    description: 'Vous débloquez un 5e emplacement de dé Maître.',
  },
  {
    id: 'arc_keep3',
    name: 'Conservation III',
    branch: 'arcane',
    parent: 'arc_slot5',
    maxLevel: 1,
    baseCost: 6000,
    description:
      'Vous conservez le dé Maître de votre emplacement 3 même après votre mort.',
  },
  // v0.26.0 — COMMENTS v0.25.0 §1.2 (A72).
  {
    id: 'arc_slot6',
    name: 'Hexadice',
    branch: 'arcane',
    parent: 'arc_keep3',
    maxLevel: 1,
    baseCost: 15000,
    description: 'Vous débloquez un 6e emplacement de dé Maître.',
  },
  {
    id: 'arc_keep4',
    name: 'Conservation IV',
    branch: 'arcane',
    parent: 'arc_slot6',
    maxLevel: 1,
    baseCost: 16000,
    description:
      'Vous conservez le dé Maître de votre emplacement 4 même après votre mort.',
  },
  {
    id: 'arc_flux',
    name: 'Flux arcanique',
    branch: 'arcane',
    parent: 'arc_sorcery',
    maxLevel: 5,
    baseCost: 15000,
    description:
      'Vos dégâts de lancer sont multipliés par 1,02 par niveau (1,10 au niveau 5), une fois ' +
      'par lancer consécutif réalisant un sort à partir du deuxième, jusqu’à 10 cumuls.',
  },
  // v0.43.0 — A89-6.
  {
    id: 'arc_rearguard',
    name: 'Arrière-garde',
    branch: 'arcane',
    parent: 'arc_sorcery',
    maxLevel: 5,
    baseCost: 12000,
    description:
      'Les sorts de dégâts portés par vos deux derniers dés Maîtres sont renforcés de 4 % par ' +
      'niveau.',
  },
  {
    id: 'arc_slot7',
    name: 'Heptadice',
    branch: 'arcane',
    parent: 'arc_keep4',
    maxLevel: 1,
    baseCost: 90000,
    description: 'Vous débloquez un 7e emplacement de dé Maître.',
  },
  {
    id: 'arc_keep5',
    name: 'Conservation V',
    branch: 'arcane',
    parent: 'arc_slot7',
    maxLevel: 1,
    baseCost: 120000,
    description:
      'Vous conservez le dé Maître de votre emplacement 5 même après votre mort.',
  },

  // ── Abîme (v0.46.0, A94) : portes 11 → 29, un nœud tous les deux niveaux.
  {
    id: 'aby_artisan',
    name: 'Artisan des dés',
    branch: 'abyss',
    parent: 'root',
    maxLevel: 1,
    baseCost: 0,
    gate: { level: 11, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Ouvre l’Abîme, la Forge (Relance, Trempage, Trucage) et les Corrupted GachaDice. ' +
      'Offert à l’entrée en mode infini.',
  },
  {
    id: 'aby_material',
    name: 'Matériau',
    branch: 'abyss',
    parent: 'aby_artisan',
    maxLevel: 1,
    baseCost: 0,
    gate: { level: 13, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Le bonus à 3 pièces des Matériaux s’active : trois dés équipés d’un même Matériau.',
  },
  {
    id: 'aby_poker',
    name: 'Qui a dit Poker ?',
    branch: 'abyss',
    parent: 'aby_material',
    maxLevel: 1,
    baseCost: 0,
    gate: { level: 15, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Débloque les sorts d’emplacement Triple paire, Double brelan, Grande suite, Maxi suite ' +
      'et Brelan carré.',
  },
  {
    id: 'aby_grimoire_numbers',
    name: 'Grimoire des nombres',
    branch: 'abyss',
    parent: 'aby_poker',
    maxLevel: 5,
    baseCost: 0,
    gate: { level: 17, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Sorts de dé : +10 % par niveau ; au niveau 3, la condition de chaque sort s’élargit ' +
      'd’un cran.',
  },
  {
    id: 'aby_enriched',
    name: 'Matériau Enrichi',
    branch: 'abyss',
    parent: 'aby_grimoire_numbers',
    maxLevel: 1,
    baseCost: 0,
    gate: { level: 19, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Les Corrupted GachaDice peuvent donner des dés Enrichis (10 % de chance) : la valeur de ' +
      'leurs affixes est multipliée par 1,5.',
  },
  {
    id: 'aby_hammer',
    name: 'Marteau Trempé',
    branch: 'abyss',
    parent: 'aby_enriched',
    maxLevel: 10,
    baseCost: 0,
    gate: { level: 21, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Le plafond de niveau des dés augmente de 2 et les recettes de forge coûtent 5 % de ' +
      'moins, par niveau.',
  },
  {
    id: 'aby_recipes',
    name: 'Carnet de recettes',
    branch: 'abyss',
    parent: 'aby_hammer',
    maxLevel: 1,
    baseCost: 0,
    gate: { level: 23, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description: 'La Forge gagne les recettes Fusion, Sublimation et Absolution.',
  },
  {
    id: 'aby_materiology',
    name: 'Matériologie',
    branch: 'abyss',
    parent: 'aby_recipes',
    maxLevel: 1,
    baseCost: 0,
    gate: { level: 25, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Caoutchouc, Silicium et Du Vide apparaissent aux Corrupted GachaDice ; le second bonus ' +
      'de Matériau, à 5 pièces, s’active.',
  },
  {
    id: 'aby_grimoire_faces',
    name: 'Face je gagne, Pile tu perds',
    branch: 'abyss',
    parent: 'aby_materiology',
    maxLevel: 5,
    baseCost: 0,
    gate: { level: 27, step: BALANCE.abyssGateStep },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    description:
      'Sorts d’emplacement : +10 % par niveau ; au niveau 3, les Suites tolèrent un trou ; au ' +
      'niveau 5, le Carré se déclenche dès trois dés identiques.',
  },
  {
    id: 'aby_bottomless',
    name: 'Sans fond',
    branch: 'abyss',
    parent: 'aby_grimoire_faces',
    maxLevel: Number.POSITIVE_INFINITY,
    baseCost: 0,
    gate: { level: 29, step: 0 },
    costExpeditions: BALANCE.abyssNodeCostExpeditions,
    repeatable: true,
    description: '+5 % de dégâts par niveau ; le prix double à chaque niveau. Sans limite.',
  },
];

export const NODE_BY_ID: Record<string, NodeDef> = Object.fromEntries(NODES.map((n) => [n.id, n]));

export const ROOT_NODE_ID = 'root';
